import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { Form, Input, InputNumber, Button, Space, Spin, Alert, message, Select, DatePicker } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  getActivity,
  createActivity,
  updateActivity,
  type CreateActivityRequest,
  type UpdateActivityRequest,
} from '@/api/activityApi';
import { listCustomers, type Customer } from '@/api/customerApi';
import { listContacts, type Contact } from '@/api/contactApi';
import { listOpportunities, type Opportunity } from '@/api/opportunityApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;

const ACTIVITY_TYPES = ['call', 'email', 'meeting', 'note', 'task'] as const;
const ACTIVITY_STATUSES = ['planned', 'in_progress', 'completed', 'cancelled'] as const;
const RELATED_TO_TYPES = ['customer', 'contact', 'opportunity'] as const;

interface ActivityFormValues {
  type: string;
  subject: string;
  content?: string;
  related_to_type: string;
  related_to_id: string;
  scheduled_at?: dayjs.Dayjs;
  completed_at?: dayjs.Dayjs;
  duration?: number;
  owner_id?: string;
  status?: string;
  notes?: string;
  tags?: string[];
}

interface EntityOption {
  value: string;
  label: string;
}

export default function ActivityForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm<ActivityFormValues>();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [relatedOptions, setRelatedOptions] = useState<EntityOption[]>([]);
  const [relatedSearching, setRelatedSearching] = useState(false);
  const [selectedRelatedType, setSelectedRelatedType] = useState<string>('');

  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  const searchRelatedEntities = useCallback(
    (keyword: string, relatedType?: string) => {
      const type = relatedType ?? selectedRelatedType;
      if (!type) {
        setRelatedOptions([]);
        return;
      }
      if (searchTimer.current) clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(async () => {
        setRelatedSearching(true);
        try {
          let options: EntityOption[] = [];
          switch (type) {
            case 'customer': {
              const res = await listCustomers({ from: 0, size: 20, keyword: keyword || undefined });
              options = res.items.map((c: Customer) => ({
                value: c.id,
                label: `${c.name}${c.company ? ` (${c.company})` : ''}`,
              }));
              break;
            }
            case 'contact': {
              const res = await listContacts({ from: 0, size: 20, keyword: keyword || undefined });
              options = res.items.map((c: Contact) => ({
                value: c.id,
                label: `${c.name}${c.email ? ` (${c.email})` : ''}`,
              }));
              break;
            }
            case 'opportunity': {
              const res = await listOpportunities({ from: 0, size: 20, keyword: keyword || undefined });
              options = res.items.map((o: Opportunity) => ({
                value: o.id,
                label: o.name,
              }));
              break;
            }
          }
          setRelatedOptions(options);
        } catch {
          setRelatedOptions([]);
        } finally {
          setRelatedSearching(false);
        }
      }, 300);
    },
    [selectedRelatedType]
  );

  const handleRelatedTypeChange = useCallback(
    (val: string) => {
      setSelectedRelatedType(val);
      form.setFieldValue('related_to_id', undefined);
      setRelatedOptions([]);
      searchRelatedEntities('', val);
    },
    [form, searchRelatedEntities]
  );

  // Load initial related options
  useEffect(() => {
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    setError(null);
    getActivity(id)
      .then((a) => {
        setSelectedRelatedType(a.related_to_type);
        form.setFieldsValue({
          type: a.type,
          subject: a.subject,
          content: a.content || undefined,
          related_to_type: a.related_to_type,
          related_to_id: a.related_to_id,
          scheduled_at: a.scheduled_at ? dayjs(a.scheduled_at) : undefined,
          completed_at: a.completed_at ? dayjs(a.completed_at) : undefined,
          duration: a.duration || undefined,
          owner_id: a.owner_id || undefined,
          status: a.status || undefined,
          notes: a.notes || undefined,
          tags: (a.tags?.length ?? 0) > 0 ? a.tags : undefined,
        });
        // Load related entity options for the current type
        searchRelatedEntities('', a.related_to_type);
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [isEdit, id, form, t, searchRelatedEntities]);

  const handleSubmit = async (values: ActivityFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && id) {
        const data: UpdateActivityRequest = {
          type: values.type,
          subject: values.subject,
          content: values.content || undefined,
          related_to_type: values.related_to_type,
          related_to_id: values.related_to_id,
          scheduled_at: values.scheduled_at?.toISOString() || undefined,
          completed_at: values.completed_at?.toISOString() || undefined,
          duration: values.duration || 0,
          owner_id: values.owner_id || undefined,
          status: values.status || undefined,
          notes: values.notes || undefined,
          tags: values.tags || undefined,
        };
        await updateActivity(id, data);
        message.success(t('activities.update_success'));
        navigate(`/activities/${id}`);
      } else {
        const data: CreateActivityRequest = {
          type: values.type,
          subject: values.subject,
          content: values.content || undefined,
          related_to_type: values.related_to_type,
          related_to_id: values.related_to_id,
          scheduled_at: values.scheduled_at?.toISOString() || undefined,
          completed_at: values.completed_at?.toISOString() || undefined,
          duration: values.duration || 0,
          owner_id: values.owner_id || undefined,
          notes: values.notes || undefined,
          tags: values.tags || undefined,
        };
        const created = await createActivity(data);
        message.success(t('activities.create_success'));
        navigate(`/activities/${created.id}`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('app.error');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!hasPermission('activities', 'write')) {
    return <Navigate to="/activities" replace />;
  }

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  const typeOptions = ACTIVITY_TYPES.map((v) => ({
    value: v,
    label: t(`activities.type_options.${v}`),
  }));

  const statusSelectOptions = ACTIVITY_STATUSES.map((v) => ({
    value: v,
    label: t(`activities.status_options.${v}`),
  }));

  const relatedTypeSelectOptions = RELATED_TO_TYPES.map((v) => ({
    value: v,
    label: t(`activities.related_to_options.${v}`),
  }));

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(isEdit ? `/activities/${id}` : '/activities')}>
          {t('app.back')}
        </Button>
      </Space>

      <h2>{isEdit ? t('activities.edit_title') : t('activities.create_title')}</h2>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} style={{ marginBottom: 16 }} />}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ duration: 0 }}
        style={{ maxWidth: 800 }}
      >
        <Form.Item
          name="type"
          label={t('activities.field.type')}
          rules={[{ required: true, message: t('activities.validation.type_required') }]}
        >
          <Select options={typeOptions} placeholder={t('activities.select_type')} />
        </Form.Item>

        <Form.Item
          name="subject"
          label={t('activities.field.subject')}
          rules={[
            { required: true, message: t('activities.validation.subject_required') },
            { max: 500, message: t('activities.validation.subject_max_length') },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="related_to_type"
          label={t('activities.field.related_to_type')}
          rules={[{ required: true, message: t('activities.validation.related_to_type_required') }]}
        >
          <Select
            options={relatedTypeSelectOptions}
            placeholder={t('activities.select_related_type')}
            onChange={handleRelatedTypeChange}
          />
        </Form.Item>

        <Form.Item
          name="related_to_id"
          label={t('activities.field.related_to_id')}
          rules={[{ required: true, message: t('activities.validation.related_to_id_required') }]}
        >
          <Select
            showSearch
            filterOption={false}
            onSearch={searchRelatedEntities}
            loading={relatedSearching}
            options={relatedOptions}
            placeholder={t('activities.select_related_entity')}
            disabled={!selectedRelatedType}
          />
        </Form.Item>

        <Form.Item
          name="content"
          label={t('activities.field.content')}
          rules={[{ max: 5000, message: t('activities.validation.content_max_length') }]}
        >
          <TextArea rows={4} />
        </Form.Item>

        <Space size="large">
          <Form.Item name="scheduled_at" label={t('activities.field.scheduled_at')}>
            <DatePicker showTime />
          </Form.Item>

          <Form.Item name="completed_at" label={t('activities.field.completed_at')}>
            <DatePicker showTime />
          </Form.Item>

          <Form.Item
            name="duration"
            label={t('activities.field.duration')}
          >
            <InputNumber min={0} max={525600} addonAfter={t('activities.duration_unit')} style={{ width: 150 }} />
          </Form.Item>
        </Space>

        <Form.Item name="owner_id" label={t('activities.field.owner_id')}>
          <Input />
        </Form.Item>

        {isEdit && (
          <Form.Item name="status" label={t('activities.field.status')}>
            <Select options={statusSelectOptions} placeholder={t('activities.select_status')} />
          </Form.Item>
        )}

        <Form.Item
          name="notes"
          label={t('activities.field.notes')}
          rules={[{ max: 2000, message: t('activities.validation.notes_max_length') }]}
        >
          <TextArea rows={3} />
        </Form.Item>

        <Form.Item name="tags" label={t('activities.field.tags')}>
          <Select mode="tags" tokenSeparators={[',']} />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate(isEdit ? `/activities/${id}` : '/activities')}>
              {t('app.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
